import z from 'zod'

import { ENDPOINT } from '@/config/endpoint'
import { apiFactory, apiOk } from '@/libs/api'
import type { WithId } from '@/types/common'

import { DeviceDto, OutletDto, OutletType } from './outlet-dto'

type OutletMutation = Pick<
  OutletDto,
  'id' | 'name' | 'code' | 'desc' | 'email' | 'phone' | 'address' | 'type'
>

export const outletMasterApi = {
  list: apiFactory({
    url: ENDPOINT.outlet.master.list,
    method: 'get',
    schema: {
      params: z
        .custom<{
          ids?: string[]
          type?: OutletType
        }>()
        .default({}),
      result: apiOk(z.object({ outlets: OutletDto.array() })),
    },
  }),
  detail: apiFactory({
    url: ENDPOINT.outlet.master.detail,
    method: 'get',
    schema: {
      params: z.custom<WithId>(),
      result: apiOk(z.object({ outlet: OutletDto })),
    },
  }),
  create: apiFactory({
    url: ENDPOINT.outlet.master.create,
    method: 'post',
    dependencies: [ENDPOINT.outlet.master.list, ENDPOINT.outlet.master.detail],
    schema: {
      body: z.custom<Omit<OutletMutation, 'id'>>(),
    },
  }),
  update: apiFactory({
    url: ENDPOINT.outlet.master.update,
    method: 'put',
    dependencies: [ENDPOINT.outlet.master.list, ENDPOINT.outlet.master.detail],
    schema: {
      body: z.custom<OutletMutation>(),
    },
  }),
}

export const outletDeviceApi = {
  list: apiFactory({
    url: ENDPOINT.outlet.device.list,
    method: 'get',
    schema: {
      params: z.object({
        outletId: z.string(),
      }),
      result: apiOk(
        z.object({
          devices: DeviceDto.array(),
        })
      ),
    },
  }),
  detail: apiFactory({
    url: ENDPOINT.outlet.device.detail,
    method: 'get',
    schema: {
      params: z.object({
        id: z.string(),
      }),
      result: apiOk(z.object({ device: DeviceDto })),
    },
  }),
  create: apiFactory({
    url: ENDPOINT.outlet.device.create,
    dependencies: [ENDPOINT.outlet.device.list],
    method: 'post',
    schema: {
      body: z.custom<Pick<DeviceDto, 'outletId' | 'name' | 'code' | 'type'>>(),
      result: apiOk(z.unknown()),
    },
  }),
  update: apiFactory({
    url: ENDPOINT.outlet.device.update,
    dependencies: [ENDPOINT.outlet.device.list],
    method: 'put',
    schema: {
      body: z.custom<Pick<DeviceDto, 'id' | 'outletId' | 'name' | 'code' | 'type'>>(),
      result: apiOk(z.unknown()),
    },
  }),
  remove: apiFactory({
    url: ENDPOINT.outlet.device.remove,
    method: 'delete',
    dependencies: [ENDPOINT.outlet.device.list],
    schema: {
      params: z.object({ id: z.string() }),
      result: apiOk(z.unknown()),
    },
  }),
}
